
import React from 'react';
import { Attachment } from '@/types/attachment';
import { Checkbox } from '@/components/ui/checkbox';
import { formatDistanceToNow } from 'date-fns';
import { Download, File, FileText, FileImage, FileArchive, FilePieChart, MessageSquare, FileAudio, FileVideo, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { simplifyFileType } from '@/utils/fileTypeUtils';

interface AttachmentItemProps {
  attachment: Attachment;
  isSelected: boolean;
  onSelect: () => void;
  onDeselect: () => void;
  onDownload: () => void;
  onPreview: () => void;
}

const AttachmentItem: React.FC<AttachmentItemProps> = ({
  attachment,
  isSelected,
  onSelect,
  onDeselect,
  onDownload,
  onPreview,
}) => {
  const isMobile = useIsMobile();
  
  // Format date as relative
  const relativeDate = formatDistanceToNow(new Date(attachment.emailDate), {
    addSuffix: true,
  });
  
  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  
  // Determine file icon based on file type
  const getFileIcon = () => {
    const simpleType = simplifyFileType(attachment.fileType, attachment.fileName);
    switch (simpleType) {
      case 'pdf':
        return <FileText className="text-red-500" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <FileImage className="text-blue-400" />;
      case 'zip':
      case 'rar':
      case '7z':
        return <FileArchive className="text-purple-500" />;
      case 'xlsx':
      case 'csv':
        return <FilePieChart className="text-green-500" />;
      case 'mp3':
      case 'wav':
      case 'ogg':
      case 'm4a':
      case 'flac':
        return <FileAudio className="text-amber-500" />;
      case 'mp4':
      case 'mov':
      case 'avi':
      case 'webm':
      case 'mkv':
        return <FileVideo className="text-indigo-500" />;
      default:
        return <File className="text-gray-500" />;
    }
  };
  
  // Handle checkbox change
  const handleCheckboxChange = (checked: boolean) => {
    if (checked) {
      onSelect();
    } else {
      onDeselect();
    }
  };
  
  // Check if the file type is previewable
  const isPreviewable = () => {
    const previewableTypes = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'mp4', 'webm', 'mp3', 'wav'];
    const simpleType = simplifyFileType(attachment.fileType, attachment.fileName);
    return previewableTypes.includes(simpleType);
  };
  
  return (
    <div 
      className={cn(
        "card card-hover group relative flex flex-col md:flex-row md:items-center gap-3 md:gap-4 transition-all duration-200 overflow-hidden",
        isSelected && "ring-2 ring-primary/30 bg-primary/5"
      )}
    >
      <div className="absolute right-3 top-3 md:hidden">
        <Checkbox 
          checked={isSelected}
          onCheckedChange={handleCheckboxChange}
          className="h-5 w-5"
        />
      </div>
      
      <div className="hidden md:flex items-center h-full px-2">
        <Checkbox 
          checked={isSelected}
          onCheckedChange={handleCheckboxChange}
          className="h-5 w-5"
        />
      </div>
      
      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
        {getFileIcon()}
      </div>
      
      <div className="flex-1 min-w-0 pr-12 md:pr-0">
        <div className="flex flex-col md:flex-row md:items-center md:gap-3">
          <h3 className="text-base font-medium truncate">
            {attachment.fileName}
          </h3>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>{formatFileSize(attachment.fileSize)}</span>
            <span className="hidden md:inline">•</span>
            <span className="hidden md:inline">{simplifyFileType(attachment.fileType, attachment.fileName).toUpperCase()}</span>
          </div>
        </div>
        
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-sm">
          <div className="flex items-center text-muted-foreground">
            <MessageSquare className="mr-1.5 h-3.5 w-3.5" />
            <span className="truncate max-w-[250px]">{attachment.emailSubject}</span>
          </div>
          <div className="hidden sm:inline-flex items-center">
            <span className="inline-flex items-center">
              <span className="text-xs text-muted-foreground">From: </span>
              <span className="ml-1 text-xs font-medium truncate max-w-[150px]">{attachment.emailSenderName} ({attachment.emailSender})</span>
            </span>
          </div>
        </div>
        
        <div className="flex items-center mt-2 md:mt-1">
          <Badge variant="outline" className="text-xs font-normal">
            {relativeDate}
          </Badge>
          <Badge 
            variant="outline" 
            className="ml-2 text-xs font-normal md:hidden"
          >
            {simplifyFileType(attachment.fileType, attachment.fileName).toUpperCase()}
          </Badge>
        </div>
      </div>
      
      <div className="flex flex-row md:pl-2 gap-2 mt-2 md:mt-0">
        {isPreviewable() && (
          <Button
            size="sm"
            variant="outline"
            className="h-8 px-3 transition-all group-hover:bg-primary/20"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onPreview();
            }}
          >
            <Eye className="mr-1 h-4 w-4" />
            <span className={isMobile ? "sr-only" : ""}>Preview</span>
          </Button>
        )}
        
        <Button
          size="sm"
          variant="outline"
          className="h-8 px-3 transition-all group-hover:bg-primary group-hover:text-white"
          onClick={onDownload}
        >
          <Download className="mr-1 h-4 w-4" />
          <span className={isMobile ? "sr-only" : ""}>Download</span>
        </Button>
      </div>
    </div>
  );
};

export default AttachmentItem;
